"use strict";
/**
 * THE rate card. Every price, rate and threshold Homero charges lives here and
 * nowhere else.
 *
 * Before this file existed the same numbers were written out in five places —
 * `apps/api/src/constants/{pricing,config}.ts`, `apps/dashboard/constants/{booking,config}.ts`,
 * `apps/booking/src/constants/pricing.ts`, and inline in `apps/landing`'s
 * "Book from $X" button — and they had already drifted:
 *
 *   - the API dated V4 to 2025-04-13 and the dashboard to 2025-04-14, so every
 *     booking created in that 24-hour window was priced V4 by the server and
 *     displayed V3 by the admin UI;
 *   - the landing page still shipped the V2 table, so its advertised price and
 *     its `?t=<price>` handoff matched nothing downstream;
 *   - the customer's cart taxed at 8.88% while everything else used 8.875%.
 *
 * Those were not oversights, they were the predictable result of copying money
 * into five files. Add a rate here and every surface gets it at once.
 *
 * All amounts are integer cents.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.isFlexible = exports.getFrequencyIntervalCount = exports.getFrequencyDiscountPercent = exports.FREQUENCY_INTERVAL_COUNT = exports.FREQUENCY_DISCOUNT_PERCENT = exports.TAX_RATE = exports.ADD_ON_COST_CENTS = exports.FLEXIBLE_DISCOUNT_CENTS = exports.getCleaningTypePriceCents = exports.getRateCard = exports.RATE_CARDS = exports.V5_FROM = exports.V4_FROM = exports.V3_FROM = exports.V2_FROM = exports.V1_FROM = exports.flatRoomPrices = exports.MAX_ROOMS = exports.ROOM_COST_CENTS = exports.CLEANING_TYPE_LEGACY_NAMES = exports.CLEANING_TYPE_LABELS = exports.CLEANING_TYPE_IDS = void 0;
exports.CLEANING_TYPE_IDS = [
    "standard",
    "special_mission",
    "super_clean",
    "move_in_out",
];
/** Human-facing labels. The API's legacy wire format uses the spaced lowercase form. */
exports.CLEANING_TYPE_LABELS = {
    standard: "Standard",
    special_mission: "Special Mission",
    super_clean: "Super Clean",
    move_in_out: "Move In / Out",
};
/** The lowercase names used by the legacy API wire format and DB rows. */
exports.CLEANING_TYPE_LEGACY_NAMES = {
    standard: "standard",
    special_mission: "special mission",
    super_clean: "super clean",
    move_in_out: "move in / out",
};
/**
 * The flat room price every card charged before room prices were configurable,
 * in cents — per bedroom and per bathroom alike.
 *
 * Still what the legacy engine charges, and must stay so: a V1-V4 booking
 * reprices on the arithmetic it was sold under (see `legacy.ts`). It is also the
 * value every entry of `flatRoomPrices()` takes, which is what lets room pricing
 * ship without moving a price.
 */
exports.ROOM_COST_CENTS = 2000;
/**
 * The most bedrooms, and the most bathrooms, the booking funnel offers.
 *
 * The room tables below have exactly this many entries. A count past the end of
 * a table has no price to charge, so the engine refuses it rather than guessing
 * — `/bookings` already rejects more than five of either.
 */
exports.MAX_ROOMS = 5;
/** Every room at the same price — how every card priced rooms until now. */
function flatRoomPrices(cents = exports.ROOM_COST_CENTS) {
    const table = new Array(exports.MAX_ROOMS).fill(cents);
    return { bedroom: [...table], bathroom: [...table] };
}
exports.flatRoomPrices = flatRoomPrices;
/**
 * Version boundaries.
 *
 * V2-V4 are taken from `apps/api/src/constants/config.ts`, which is the
 * authoritative copy because it is the one that computed the charge. The
 * dashboard's V4 (2025-04-14) was a day late and is not reproduced here.
 */
exports.V1_FROM = new Date(0);
exports.V2_FROM = new Date("2022-07-20T13:16:12.346Z");
exports.V3_FROM = new Date("2022-10-24T09:36:45.000Z");
exports.V4_FROM = new Date("2025-04-13T08:52:00.000Z");
/**
 * Decimal pricing goes live at midnight 2026-07-26 in America/New_York.
 *
 * Homero operates in NYC, so the business day boundary is Eastern wall-clock
 * time. July is EDT (UTC-4), which puts the cutover at 04:00Z. Using a bare
 * UTC midnight would flip prices at 8pm ET on the 25th — customers would get
 * "tomorrow's" prices four hours early.
 */
exports.V5_FROM = new Date("2026-08-02T04:00:00.000Z");
exports.RATE_CARDS = [
    {
        version: "V1",
        effectiveFrom: exports.V1_FROM,
        supportsDecimals: false,
        roomCents: flatRoomPrices(),
        cleaningTypes: {
            standard: 8800,
            special_mission: 13800,
            super_clean: 22800,
            move_in_out: 20800,
        },
    },
    {
        version: "V2",
        effectiveFrom: exports.V2_FROM,
        supportsDecimals: false,
        roomCents: flatRoomPrices(),
        cleaningTypes: {
            standard: 10800,
            special_mission: 15800,
            super_clean: 24800,
            move_in_out: 22800,
        },
    },
    {
        version: "V3",
        effectiveFrom: exports.V3_FROM,
        supportsDecimals: false,
        roomCents: flatRoomPrices(),
        cleaningTypes: {
            standard: 10800,
            special_mission: 15800,
            super_clean: 26300,
            move_in_out: 24800,
        },
    },
    {
        version: "V4",
        effectiveFrom: exports.V4_FROM,
        supportsDecimals: false,
        roomCents: flatRoomPrices(),
        cleaningTypes: {
            standard: 12800,
            special_mission: 18300,
            super_clean: 28800,
            move_in_out: 27300,
        },
    },
    {
        /**
         * V5 carries the V4 numbers deliberately.
         *
         * Phase 1 ships the *capability* to charge cents, not a new rate card. The
         * ~30% increase in the pricing overhaul doc is a conversion decision that
         * needs sign-off, and bundling it into this migration would make it
         * impossible to tell a rounding bug from an intended price change. Once the
         * new rates are signed off, editing the numbers below is the whole change.
         */
        version: "V5",
        effectiveFrom: exports.V5_FROM,
        supportsDecimals: true,
        roomCents: flatRoomPrices(),
        cleaningTypes: {
            standard: 12800,
            special_mission: 18300,
            super_clean: 28800,
            move_in_out: 27300,
        },
    },
];
/** The rate card in force for a booking created at `createdAt`. */
function getRateCard(createdAt) {
    if (!(createdAt instanceof Date) || Number.isNaN(createdAt.getTime())) {
        throw new RangeError(`A valid created_at is required to select a rate card, got ${String(createdAt)}. ` +
            `Defaulting to the current time would reprice an existing booking.`);
    }
    let match = exports.RATE_CARDS[0];
    for (const card of exports.RATE_CARDS) {
        if (createdAt.getTime() >= card.effectiveFrom.getTime())
            match = card;
    }
    return match;
}
exports.getRateCard = getRateCard;
/** Base price for a cleaning type, in cents, for the card in force at `createdAt`. */
function getCleaningTypePriceCents(cleaningType, createdAt) {
    return getRateCard(createdAt).cleaningTypes[cleaningType];
}
exports.getCleaningTypePriceCents = getCleaningTypePriceCents;
/** Flat discount for accepting a flexible arrival window, in cents. */
exports.FLEXIBLE_DISCOUNT_CENTS = 1000;
/** Add-on prices, in cents. */
exports.ADD_ON_COST_CENTS = {
    organizing: 4000,
    oven: 3500,
    fridge: 3500,
    cabinets: 5200,
    windows: 3700,
    laundry: 3700,
};
/**
 * NYC combined sales tax, 8.875%, as an exact rational so it never becomes the
 * float 0.08875. The customer's cart previously used 8.88% here, which agreed
 * with the server only because integer subtotals hid the difference.
 */
exports.TAX_RATE = { numerator: 8875, denominator: 100000 };
/** Frequency discount, as a whole percentage of the base. */
exports.FREQUENCY_DISCOUNT_PERCENT = {
    "one time": 0,
    weekly: 20,
    "every 2 weeks": 15,
    "every 3 weeks": 10,
    "every 4 weeks": 10,
};
/** Stripe billing interval count per frequency. */
exports.FREQUENCY_INTERVAL_COUNT = {
    "one time": 0,
    weekly: 1,
    "every 2 weeks": 2,
    "every 3 weeks": 3,
    "every 4 weeks": 4,
};
function getFrequencyDiscountPercent(frequency) {
    const key = frequency.toLowerCase();
    const percent = exports.FREQUENCY_DISCOUNT_PERCENT[key];
    if (percent === undefined)
        throw new Error("Invalid frequency");
    return percent;
}
exports.getFrequencyDiscountPercent = getFrequencyDiscountPercent;
function getFrequencyIntervalCount(frequency) {
    const key = frequency.toLowerCase();
    const count = exports.FREQUENCY_INTERVAL_COUNT[key];
    if (count === undefined)
        throw new Error("Invalid frequency");
    return count;
}
exports.getFrequencyIntervalCount = getFrequencyIntervalCount;
/** Whether a clean time string means the flexible 9am-4pm window. */
function isFlexible(cleanTime) {
    return (cleanTime || "").toLowerCase() === "flexible";
}
exports.isFlexible = isFlexible;
//# sourceMappingURL=rates.js.map